import { SpiEnvironment, SpiSdk } from './types'

function getScriptNode(src: string) {
  return document.querySelector<HTMLScriptElement>(`script[src="${src}"]`)
}

function getSpiSdkSrc(env: SpiEnvironment) {
  switch (env) {
    case 'DEV':
      return 'https://dev.eng.toastteam.com/loader.js'
    case 'SANDBOX':
      return 'https://sandbox-spi-manifest.s3.amazonaws.com/loader.js'
    case 'PREPROD':
      return 'https://preproduction-spi-manifest.s3.amazonaws.com/loader.js'
    case 'PRODUCTION':
    default:
      return 'https://payments.toasttab.com/assets/loader.js'
  }
}

let moduleSpiPromise: Promise<SpiSdk> | undefined
export const clearSpiCache = () => {
  moduleSpiPromise = undefined
}

export function loadSpi(spiEnv: SpiEnvironment): Promise<SpiSdk> {
  // 1. Simply check if Toast is already on the window
  if (window.Toast) {
    return Promise.resolve(window.Toast)
  }

  // 2. Check if a result promise is cached from a previous call
  if (moduleSpiPromise) {
    return moduleSpiPromise
  }

  const scriptNodeSrc = getSpiSdkSrc(spiEnv)

  // 3. Check if the script was already loaded from another module, and look
  // for the Toast object on the window.
  const existingScriptNode = getScriptNode(scriptNodeSrc)
  if (existingScriptNode) {
    const ToastSdk = window.Toast
    if (!ToastSdk) {
      throw new Error(
        'The SPI SDK script is already loaded on the page, ' +
          'but we cannot find the corresponding window.Toast object. ' +
          'We are unable to load it twice without accounting for ' +
          'unintentional side-effects.'
      )
    }
  }

  // track event callbacks for later cleanup
  const eventCbs: ((ev: Event) => void)[] = []
  const scriptNode = document.createElement('script')
  scriptNode.type = 'text/javascript'
  scriptNode.src = scriptNodeSrc
  scriptNode.async = true
  moduleSpiPromise = new Promise<SpiSdk>((resolve, reject) => {
    scriptNode.addEventListener('load', resolvePromiseFromEvent)
    function resolvePromiseFromEvent(event: Event) {
      const scriptStatus = event.type === 'load' ? 'ready' : 'error'
      if (scriptStatus === 'error') {
        reject(new Error(`Failed to load script; src: ${scriptNodeSrc}.`))
      }
      const ToastSdk = window.Toast
      if (!ToastSdk) {
        reject(new Error('Failed to load Toast SDK on window'))
      } else {
        resolve(ToastSdk)
      }
    }
    scriptNode.addEventListener('error', resolvePromiseFromEvent)
    eventCbs.push(resolvePromiseFromEvent)
  })

  // mount the script
  document.body.appendChild(scriptNode)

  // return Promise which resolves to SDK
  return moduleSpiPromise.finally(() => {
    // clean up event listeners after resolution completes
    eventCbs.forEach((cb) => {
      scriptNode?.removeEventListener('load', cb)
      scriptNode?.removeEventListener('error', cb)
    })
  })
}
